import { lightPalette, darkPalette } from "../../../../theme/palette";
import type { PaletteColorOptions } from "@mui/material/styles";
import type {
  BrandingFormState,
} from "../types";
import type { ClientBranding, UpdateClientBrandingRequest } from "../../../../shared/api";

export const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const toPaletteString = (palette?: string[] | null) =>
  palette?.length ? palette.join(", ") : "";

export const toFormState = (branding: ClientBranding | null): BrandingFormState => ({
  primary_color: branding?.primary_color ?? "",
  secondary_color: branding?.secondary_color ?? "",
  accent_color: branding?.accent_color ?? "",
  background_color: branding?.background_color ?? "",
  text_color: branding?.text_color ?? "",
  dark_primary_color: branding?.dark_primary_color ?? "",
  dark_secondary_color: branding?.dark_secondary_color ?? "",
  dark_accent_color: branding?.dark_accent_color ?? "",
  dark_background_color: branding?.dark_background_color ?? "",
  dark_text_color: branding?.dark_text_color ?? "",
  chart_primary_color: branding?.chart_primary_color ?? "",
  chart_secondary_color: branding?.chart_secondary_color ?? "",
  chart_accent_color: branding?.chart_accent_color ?? "",
  chart_palette: toPaletteString(branding?.chart_palette ?? null),
  dark_chart_primary_color: branding?.dark_chart_primary_color ?? "",
  dark_chart_secondary_color: branding?.dark_chart_secondary_color ?? "",
  dark_chart_accent_color: branding?.dark_chart_accent_color ?? "",
  dark_chart_palette: toPaletteString(branding?.dark_chart_palette ?? null),
  logo_url: branding?.logo_url ?? "",
  logo_dark_url: branding?.logo_dark_url ?? "",
  dark_logo_url: branding?.dark_logo_url ?? "",
});

export const parsePalette = (value: string) => {
  const entries = value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
  if (!entries.length) return null;
  return entries;
};

export const validateHex = (value: string) =>
  value.trim().length === 0 || HEX_COLOR_REGEX.test(value.trim());

export const validatePalette = (value: string) => {
  const entries = parsePalette(value);
  if (!entries) return { valid: true };
  if (entries.length < 3 || entries.length > 8) {
    return { valid: false, message: "Use 3 to 8 colours." };
  }
  const invalid = entries.find((color) => !HEX_COLOR_REGEX.test(color));
  if (invalid) {
    return { valid: false, message: `Invalid colour: ${invalid}` };
  }
  return { valid: true };
};

export const buildPayload = (form: BrandingFormState): UpdateClientBrandingRequest => {
  const trimOrNull = (value: string) =>
    value.trim().length === 0 ? null : value.trim();

  return {
    primary_color: trimOrNull(form.primary_color),
    secondary_color: trimOrNull(form.secondary_color),
    accent_color: trimOrNull(form.accent_color),
    background_color: trimOrNull(form.background_color),
    text_color: trimOrNull(form.text_color),
    dark_primary_color: trimOrNull(form.dark_primary_color),
    dark_secondary_color: trimOrNull(form.dark_secondary_color),
    dark_accent_color: trimOrNull(form.dark_accent_color),
    dark_background_color: trimOrNull(form.dark_background_color),
    dark_text_color: trimOrNull(form.dark_text_color),
    chart_primary_color: trimOrNull(form.chart_primary_color),
    chart_secondary_color: trimOrNull(form.chart_secondary_color),
    chart_accent_color: trimOrNull(form.chart_accent_color),
    chart_palette: parsePalette(form.chart_palette),
    dark_chart_primary_color: trimOrNull(form.dark_chart_primary_color),
    dark_chart_secondary_color: trimOrNull(form.dark_chart_secondary_color),
    dark_chart_accent_color: trimOrNull(form.dark_chart_accent_color),
    dark_chart_palette: parsePalette(form.dark_chart_palette),
    logo_url: trimOrNull(form.logo_url),
    logo_dark_url: trimOrNull(form.logo_dark_url),
    dark_logo_url: trimOrNull(form.dark_logo_url),
  };
};

export const makePreview = (form: BrandingFormState, mode: "light" | "dark") => {
  const palette = mode === "light" ? lightPalette : darkPalette;
  const isDark = mode === "dark";
  const paletteMain = (color?: PaletteColorOptions) =>
    color && "main" in color ? color.main : undefined;

  const background =
    (isDark ? form.dark_background_color : form.background_color) ||
    (palette.background?.default ?? "#ffffff");
  const text =
    (isDark ? form.dark_text_color : form.text_color) ||
    (palette.text?.primary ?? "#101820");
  const primary =
    (isDark ? form.dark_primary_color : form.primary_color) ||
    (paletteMain(palette.primary) ?? "#00615F");
  const secondary =
    (isDark ? form.dark_secondary_color : form.secondary_color) ||
    (paletteMain(palette.secondary) ?? "#1F6600");
  const accent = isDark
    ? form.dark_accent_color || form.accent_color || secondary
    : form.accent_color || secondary;
  const chartPalette = parsePalette(
    isDark ? form.dark_chart_palette : form.chart_palette
  );

  return {
    background,
    text,
    primary,
    secondary,
    accent,
    chartPalette: chartPalette ?? [],
    logoUrl: isDark
      ? form.dark_logo_url || form.logo_dark_url || form.logo_url
      : form.logo_url,
  };
};
